// Metryka umowy wg Centralnego Rejestru Umów (CRU) — lustro BE
// Kwantum.LegalMail.Data.Registers.ContractCruDtos (port ContractCruMetric z Magistratu).
// Enumy są serializowane kodami API CRU ("SU01", "TSU02", "dzień", ...).

/** Jednostka okresu obowiązywania (CRU: okresJednostka). */
export type CruPeriodUnit = "dzień" | "miesiąc" | "rok";

/** Rodzaj strony: SU01 przedsiębiorca, SU02 osoba fizyczna, SU03 JSFP. */
export type CruSideType = "SU01" | "SU02" | "SU03";

/** Podstawa wyłączenia jawności (art. 5 u.d.i.p.). */
export type CruConfidentialityBasis =
  | "ART_5_UST_1"
  | "ART_5_UST_2"
  | "ART_5_UST_2A"
  | "ART_5_UST_2B"
  | "INNA";

/** Rodzaj zmiany umowy (CRU: rodzajZmiany). */
export type CruChangeType =
  | "TSU01"
  | "TSU02"
  | "TSU03"
  | "TSU04"
  | "TSU05"
  | "TSU06"
  | "TSU07"
  | "TSU10"
  | "TSU11"
  | "inne";

export type CruAgreementStatus = "Aktywna" | "Nieaktywna";

export type CruHistoryAction =
  | "Created"
  | "Published"
  | "Updated"
  | "Failed"
  | "Removed"
  | "Withdrawn";

export type ContractSourceKind =
  | "Manual"
  | "EditorDocument"
  | "IncomingParcelAttachment";

export interface IContractConfidentiality {
  Basis: CruConfidentialityBasis;
  /** Komentarz — wymagany i przekazywany do CRU tylko dla podstawy „INNA". */
  Comment?: string | null;
  /** Organ lub osoba wyłączająca jawność. */
  Authority?: string | null;
}

export interface IContractCruSide {
  Id?: string | null;
  SideOrder?: number | null;
  Country: string;
  SideType: CruSideType;
  IsConsortium: boolean;
  Name?: string | null;
  Regon?: string | null;
  Nip?: string | null;
  FirstName?: string | null;
  LastName?: string | null;
  Street?: string | null;
  BuildingNumber?: string | null;
  ApartmentNumber?: string | null;
  Voivodeship?: string | null;
  County?: string | null;
  Municipality?: string | null;
  City?: string | null;
  PostalCode?: string | null;
  Confidentiality?: IContractConfidentiality | null;
  SourceEntityId?: string | null;
}

export interface IContractChange {
  Id?: string | null;
  ChangeType: CruChangeType;
  /** ISO date. */
  ChangeDate: string;
  Comment?: string | null;
}

export interface IContractCruMetric {
  Id?: string | null;
  IsCruContract: boolean;

  // krok 1
  AgreementNumber?: string | null;
  HasNoAgreementNumber: boolean;
  InternalUnit?: string | null;
  AgreementDate?: string | null;
  IndefinitePeriod: boolean;
  PeriodCount?: number | null;
  PeriodUnit?: CruPeriodUnit | null;
  PeriodFrom?: string | null;
  PeriodTo?: string | null;

  // krok 2
  Sides: IContractCruSide[];

  // krok 3
  Subject?: string | null;
  Value?: number | null;
  ValueDescription?: string | null;
  FinancedFromOtherFunds?: boolean | null;
  SubjectConfidentiality?: IContractConfidentiality | null;
  ValueConfidentiality?: IContractConfidentiality | null;

  // krok 4
  AgreementStatus?: CruAgreementStatus | null;
  AgreementEndDate?: string | null;
  Changes: IContractChange[];

  // stan publikacji (tylko odczyt)
  CruAgreementId?: string | null;
  ExternalSystemId?: string | null;
  CruSystemNumber?: number | null;
  CruProcessStatus?: string | null;
  CruPublicationDate?: string | null;
  CruLastUpdateDate?: string | null;
  CruWithdrawn?: boolean;
  IsPublished?: boolean;
}

export interface IContractCruHistory {
  Id: string;
  Action: CruHistoryAction;
  Timestamp: string;
  UserId?: string | null;
  UserName?: string | null;
  CruSystemNumber?: number | null;
  Message?: string | null;
  Errors?: Record<string, string> | null;
}

export interface ICruPublishResult {
  Success: boolean;
  Message?: string | null;
  CruAgreementId?: string | null;
  CruProcessStatus?: string | null;
  Errors?: Record<string, string> | null;
}

export interface ICruSettings {
  HasApiKey: boolean;
  KeyCreationDate?: string | null;
  KeyValidTo?: string | null;
  IsActive: boolean;
  InstitutionRegon?: string | null;
  Environment: "Test" | "Production";
  PublicationStart: string;
}

// ── Etykiety (port CruOptions z Magistratu) ────────────────────────────────

export const CRU_SIDE_TYPE_LABELS: Record<CruSideType, string> = {
  SU01: "Przedsiębiorca",
  SU02: "Osoba fizyczna",
  SU03: "JSFP",
};

export const CRU_PERIOD_UNIT_LABELS: Record<CruPeriodUnit, string> = {
  dzień: "dzień",
  miesiąc: "miesiąc",
  rok: "rok",
};

export const CRU_STATUS_LABELS: Record<CruAgreementStatus, string> = {
  Aktywna: "Aktywna",
  Nieaktywna: "Nieaktywna",
};

export const CRU_CONFIDENTIALITY_LABELS: Record<CruConfidentialityBasis, string> =
  {
    ART_5_UST_1: "Art. 5 ust. 1",
    ART_5_UST_2: "Art. 5 ust. 2",
    ART_5_UST_2A: "Art. 5 ust. 2a",
    ART_5_UST_2B: "Art. 5 ust. 2b",
    INNA: "Inna podstawa",
  };

export const CRU_CHANGE_TYPE_LABELS: Record<CruChangeType, string> = {
  TSU01: "Korekta danych",
  TSU02: "Aneks do umowy",
  TSU03: "Zmiana wyłączenia jawności",
  TSU04: "Zmiana danych strony umowy",
  TSU05: "Rozwiązanie umowy",
  TSU06: "Wypowiedzenie umowy",
  TSU07: "Odstąpienie od umowy",
  TSU10: "Wygaśnięcie umowy",
  TSU11: "Cesja umowy",
  inne: "Inne",
};

export const CRU_HISTORY_ACTION_LABELS: Record<CruHistoryAction, string> = {
  Created: "Utworzono metrykę",
  Published: "Opublikowano w CRU",
  Updated: "Zaktualizowano w CRU",
  Failed: "Błąd",
  Removed: "Usunięto",
  Withdrawn: "Wycofano z publikacji",
};

export const CONTRACT_SOURCE_LABELS: Record<ContractSourceKind, string> = {
  Manual: "Rejestr",
  EditorDocument: "Pismo",
  IncomingParcelAttachment: "Przesyłka",
};

/** Pusta metryka (nowa umowa w formacie CRU). */
export const emptyCruMetric = (subject = ""): IContractCruMetric => ({
  IsCruContract: true,
  HasNoAgreementNumber: false,
  IndefinitePeriod: false,
  Sides: [],
  Subject: subject,
  AgreementStatus: "Aktywna",
  Changes: [],
});

export const emptyCruSide = (): IContractCruSide => ({
  Country: "PL",
  SideType: "SU01",
  IsConsortium: false,
});
